import React, { useRef, useLayoutEffect, useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import PropTypes from 'prop-types';
import { withTheme } from 'common/styling/theme';
import { ReloadIcon } from 'common/icons';
import { initialBOTableState } from 'common/constants/tables';
import { TableBody, TableVisibility, TableHeader, TableLoader } from './_components';
import { staticStyles, getDynamicStyles } from './style';

const NewBaseTable = ({ table, tableKey, theme, loading, handleRefresh, emptyMessage }) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const tableRef = useRef(null);
  const [tableHeight, setTableHeight] = useState(0);

  const tableVisibleColsKey = table
    .getVisibleLeafColumns()
    .map(c => c.id)
    .join('|');

  const colSizes = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes = {
      '--first-visible-column-size': table.getVisibleLeafColumns()[0]?.getSize() || 0,
    };
    const colSizesKeysMap = Object.fromEntries(table.getAllLeafColumns().map(column => [column.id, column.getSize()]));

    for (let i = 0; i < headers.length; i += 1) {
      const header = headers[i];
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }

    const savedTableState = JSON.parse(localStorage.getItem(tableKey));

    localStorage.setItem(tableKey, JSON.stringify({ ...savedTableState, colSizes: colSizesKeysMap }));

    return colSizes;
  }, [
    table.getState().columnSizingInfo,
    table.getState().columnSizing,
    table.getState().columnVisibility,
    tableVisibleColsKey,
  ]);

  const totalTableWidth = useMemo(
    () => table.getTotalSize(),
    [table.getState().columnSizing, table.getState().columnVisibility]
  );

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor), useSensor(KeyboardSensor));
  const columnOrder = table.getState().columnOrder;

  const handleDragEnd = ({ active, over }) => {
    if (!active?.id || !over?.id || active.id === over.id) {
      return;
    }

    const oldIndex = columnOrder.indexOf(active.id);
    const newIndex = columnOrder.indexOf(over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const next = [...columnOrder];
    const [moved] = next.splice(oldIndex, 1);
    next.splice(newIndex, 0, moved);

    table.setColumnOrder(next);
  };

  const handleRefreshTableState = () => {
    table.resetColumnVisibility();
    table.resetColumnOrder();
    table.resetColumnSizing();

    handleRefresh();

    localStorage.setItem(tableKey, JSON.stringify(initialBOTableState));
  };

  useLayoutEffect(() => {
    const table = tableRef.current;

    if (table) {
      const tableHeight = table.getBoundingClientRect().height;

      setTableHeight(tableHeight);
    }
  }, [loading, tableVisibleColsKey]);

  return (
    <div>
      {!loading && (
        <>
          <div className="NewBaseTable__actions">
            <TableVisibility columns={table.getAllLeafColumns()} tableVisibleColsKey={tableVisibleColsKey} />
            <button type="button" onClick={handleRefreshTableState} className="NewBaseTable__actions-reload">
              <ReloadIcon />
            </button>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            onDragEnd={handleDragEnd}>
            <div className="NewBaseTable__wrapper">
              <table className="NewBaseTable" style={{ width: totalTableWidth, ...colSizes }} ref={tableRef}>
                <TableHeader table={table} tableVisibleColsKey={tableVisibleColsKey} tableHeight={tableHeight} />
                <TableBody emptyMessage={emptyMessage} table={table} tableVisibleColsKey={tableVisibleColsKey} />
              </table>
            </div>
          </DndContext>
        </>
      )}
      {loading && <TableLoader />}

      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </div>
  );
};

NewBaseTable.propTypes = {
  emptyMessage: PropTypes.node,
  table: PropTypes.object.isRequired,
  tableKey: PropTypes.string.isRequired,
  handleRefresh: PropTypes.func,
  theme: PropTypes.object,
  loading: PropTypes.bool,
};

NewBaseTable.defaultProps = {
  emptyMessage: null,
  theme: {},
  loading: false,
  handleRefresh: () => {},
};

export default withTheme()(NewBaseTable);
export { NewBaseTable };
